import { Link } from 'react-router-dom';
import { ArrowUp, ArrowUpRight, Mail, Phone } from 'lucide-react';
import { NAV_LINKS, SERVICES, CONTACT } from '../constants';
import WhatsAppIcon from '../components/WhatsAppIcon';

const COMPANY = NAV_LINKS.filter((l) => l.href !== '/services');
const colHead = 'text-xs font-semibold uppercase tracking-[0.16em] text-white/70';
const linkCls = 'text-white/80 transition-colors hover:text-[#4FB0E7]';

export default function Footer() {
  const toTop = () => {
    if (window.lenis) window.lenis.scrollTo(0);
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer aria-label="Farwings Tech Solution footer" className="relative overflow-hidden bg-[#060D1B] text-white">
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[420px] w-[820px] -translate-x-1/2 rounded-full bg-[#4FB0E7]/10 blur-[120px]" aria-hidden="true" />

      {/* call to action */}
      <div className="frame relative pt-20 sm:pt-24">
        <div className="flex flex-col items-start justify-between gap-8 rounded-3xl border border-white/15 bg-gradient-to-br from-white/[0.06] to-transparent p-8 sm:p-12 md:flex-row md:items-center">
          <h2 className="display max-w-[16ch] text-4xl leading-[1.02] sm:text-5xl">
            Got a problem? <span className="text-[#4FB0E7]">Let&rsquo;s fix it.</span>
          </h2>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link to="/contact" className="btn bg-white text-[#07101F] hover:bg-[#4FB0E7]">
              Start a project <ArrowUpRight size={18} aria-hidden="true" />
            </Link>
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="btn bg-[#25D366] text-[#07101F] hover:brightness-110"
            >
              <WhatsAppIcon size={18} />
              WhatsApp us
            </a>
          </div>
        </div>
      </div>

      {/* links */}
      <div className="frame relative mt-16 grid grid-cols-2 gap-x-6 gap-y-12 md:grid-cols-12">
        <div className="col-span-2 md:col-span-5">
          <Link to="/" className="inline-flex items-center gap-2.5" aria-label="Farwings home">
            <img src="/farwings-mark.png" alt="" width="40" height="40" className="h-10 w-10 object-contain" />
            <span className="display-semi text-2xl tracking-[-0.02em]">Farwings</span>
          </Link>
          <p className="mt-5 max-w-[34ch] text-lg font-semibold leading-snug">
            Your vision. Our technology. Real solutions.
          </p>
          <p className="mt-3 max-w-[40ch] text-white/80">
            App and web development company in Gujarat, India, working with clients worldwide.
          </p>
        </div>

        <nav aria-label="Services" className="col-span-2 sm:col-span-1 md:col-span-3">
          <h2 className={colHead}>Services</h2>
          <ul className="mt-5 space-y-3">
            {SERVICES.map((s) => (
              <li key={s.id}>
                <Link to={s.link} className={linkCls}>
                  {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <nav aria-label="Footer" className="col-span-1 md:col-span-2">
          <h2 className={colHead}>Company</h2>
          <ul className="mt-5 space-y-3">
            {COMPANY.map((l) => (
              <li key={l.href}>
                <Link to={l.href} className={linkCls}>
                  {l.name}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="col-span-1 md:col-span-2">
          <h2 className={colHead}>Contact</h2>
          <ul className="mt-5 space-y-3">
            <li>
              <a href={`mailto:${CONTACT.email}`} className={`${linkCls} inline-flex items-center gap-2 break-all`}>
                <Mail size={15} aria-hidden="true" className="shrink-0 text-white/70" />
                Email us
              </a>
            </li>
            {CONTACT.phones.map((ph) => (
              <li key={ph}>
                <a href={`tel:${ph.replace(/\s/g, '')}`} className={`${linkCls} inline-flex items-center gap-2 whitespace-nowrap`}>
                  <Phone size={15} aria-hidden="true" className="shrink-0 text-white/70" />
                  {ph}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* big wordmark, fully visible, with a slow light sweep */}
      <div className="footer-glow frame relative mt-16 border-t border-white/15 pt-10">
        <p
          aria-hidden="true"
          className="footer-word display select-none whitespace-nowrap text-center text-[21vw] leading-[0.85] lg:text-[13.5rem] xl:text-[15.5rem]"
        >
          FARWINGS
        </p>
      </div>

      {/* bottom bar, under the wordmark */}
      <div className="frame relative flex flex-col items-center gap-4 pb-10 pt-6 text-center text-sm text-white/75">

        <button
          type="button"
          onClick={toTop}
          className="inline-flex w-fit items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-white transition-colors hover:border-[#4FB0E7] hover:text-[#4FB0E7]"
        >
          Back to top <ArrowUp size={15} aria-hidden="true" />
        </button>
        <p>&copy; {new Date().getFullYear()} Farwings Tech Solution. Made in India.</p>
      </div>
    </footer>
  );
}
